import type { ReactNode } from 'react';
import {
  ArrowsLeftRightIcon,
  CrownSimpleIcon,
  DotsThreeIcon,
  UserPlusIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Badge } from '@voll-intelligence/ui-kit/ui/badge';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@voll-intelligence/ui-kit/ui/dropdown-menu';
import type { HotelTravelerOption } from '../models.js';
import { shortPersonName } from '../../shared/person/person_name.js';
import { PersonAvatar } from '../../shared/person/person_row.js';
import type { HotelRoomsFieldCopy } from './hotel_rooms_field.js';
import { findTraveler } from './hotel_rooms_travelers.js';

/*
 * Uma VAGA de quarto no campo QUEM do hotel: a linha flat da pessoa (retrato,
 * nome curto e o selo «Titular» no primeiro do quarto, em nome de quem a
 * reserva sai) e as ações no menu ⋯. Separada de `hotel_rooms_field.tsx` pelo
 * teto de linhas dele.
 */

/** As ações da vaga, cada uma só quando faz sentido: tornar titular não existe
 *  para quem já é, remover não existe para a última vaga vazia. */
function SlotMenu({
  name,
  copy,
  holder,
  removable,
  fill,
  onMakeHolder,
  onChoose,
  onRemove,
}: {
  readonly name: string;
  readonly copy: HotelRoomsFieldCopy;
  readonly holder: boolean;
  readonly removable: boolean;
  readonly fill: boolean;
  readonly onMakeHolder: () => void;
  readonly onChoose: () => void;
  readonly onRemove: () => void;
}): ReactNode {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground data-popup-open:bg-muted data-popup-open:text-foreground"
            aria-label={copy.guestOptionsLabel(name)}
          />
        }
      >
        <DotsThreeIcon weight="bold" aria-hidden="true" />
      </DropdownMenuTrigger>
      {/* No popover o menu abre AO LADO, fora do painel, como um submenu; na
          folha do celular não há lado — ele desce sob o ⋯. */}
      <DropdownMenuContent
        side={fill ? 'bottom' : 'inline-end'}
        align={fill ? 'end' : 'start'}
        sideOffset={fill ? 4 : 12}
      >
        {!holder && (
          <DropdownMenuItem onClick={onMakeHolder}>
            <CrownSimpleIcon aria-hidden="true" />
            {copy.makeHolderLabel}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onChoose}>
          <ArrowsLeftRightIcon aria-hidden="true" />
          {copy.swapGuestLabel}
        </DropdownMenuItem>
        {removable && (
          <DropdownMenuItem onClick={onRemove}>
            <XIcon aria-hidden="true" />
            {copy.removeFromRoomLabel}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function GuestSlot({
  travelerId,
  slotIndex,
  roomNumber,
  full,
  travelers,
  copy,
  disabled,
  removable,
  fill,
  onChoose,
  onMakeHolder,
  onRemove,
}: {
  readonly travelerId: string | undefined;
  readonly slotIndex: number;
  readonly roomNumber: number;
  /** O teto de hóspedes nomeados: a vaga vazia não aceita mais ninguém. */
  readonly full: boolean;
  readonly travelers: readonly HotelTravelerOption[];
  readonly copy: HotelRoomsFieldCopy;
  readonly disabled: boolean;
  readonly removable: boolean;
  readonly fill: boolean;
  readonly onChoose: () => void;
  readonly onMakeHolder: () => void;
  readonly onRemove: () => void;
}): ReactNode {
  if (travelerId === undefined) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-1 h-9 justify-start gap-2 px-1 font-normal text-muted-foreground hover:text-foreground"
        disabled={disabled || full}
        aria-label={`${copy.chooseGuestLabel} — ${copy.roomLabel(roomNumber)}`}
        onClick={onChoose}
      >
        <span className="flex size-6 items-center justify-center rounded-full border border-dashed border-border">
          <UserPlusIcon aria-hidden="true" />
        </span>
        <span>{copy.chooseGuestLabel}</span>
      </Button>
    );
  }
  const traveler = findTraveler(travelerId, travelers);
  const name = traveler?.label ?? travelerId;
  const holder = slotIndex === 0;
  return (
    <div className="flex h-9 min-w-0 items-center gap-2" title={name}>
      <PersonAvatar name={name} photoUrl={traveler?.photoUrl} size="sm" />
      <span className="text-ui min-w-0 truncate">{shortPersonName(name)}</span>
      {holder && (
        <Badge variant="soft" className="shrink-0">
          {copy.holderBadgeLabel}
        </Badge>
      )}
      <span className="flex-1" />
      {!disabled && (
        <SlotMenu
          name={name}
          copy={copy}
          holder={holder}
          removable={removable}
          fill={fill}
          onMakeHolder={onMakeHolder}
          onChoose={onChoose}
          onRemove={onRemove}
        />
      )}
    </div>
  );
}
